import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/primitives/utils"

const buttonVariants = cva(
  // beacon: 13px medium text, a 100ms colour-only transition on the snappy curve, a solid 2px
  // offset focus ring (at /50 it fell short of 3:1), and no press translate (nothing moves on hover
  // or press). A Kbd inside sits 4px from the label: the Kbd as a whole, so a stroke of two chips
  // (⌘ K) keeps its own 2px between them.
  "group/button inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md text-ui font-medium whitespace-nowrap transition-[color,background-color,box-shadow] duration-100 ease-snappy outline-none select-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none aria-invalid:ring-2 aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 **:data-[slot=kbd-group]:ml-1",
  {
    variants: {
      // beacon: Beacon's variant set. Solid fills carry the button shadow; a disabled solid button
      // takes the quiet muted fill rather than a faded brand colour. `destructive` is the solid
      // confirming button; `destructive-ghost` is a quiet control that turns red only on hover (a
      // row's ×). A Kbd inside a solid button inverts onto the fill, its chord's "then" too. Once
      // the button is disabled and its fill goes muted, the chips drop their own fill and take the
      // label's quiet colour, so the whole button reads as one faded thing rather than a grey
      // button holding white chips. A pending button (`focusableWhenDisabled`) isn't `:disabled`,
      // so it keeps both. A
      // ghost button on anything floating (a dialog's close, a popover's controls) hovers in the
      // popover's accent, since the panel's sits below a dark popover and would darken it.
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-button hover:bg-primary-hover disabled:bg-muted disabled:text-subtle-foreground disabled:shadow-none **:data-[slot=kbd]:bg-primary-foreground/15 **:data-[slot=kbd]:text-primary-foreground **:data-[slot=kbd]:ring-0 **:data-[slot=kbd-then]:text-primary-foreground/80 disabled:**:data-[slot=kbd]:bg-transparent disabled:**:data-[slot=kbd]:text-subtle-foreground disabled:**:data-[slot=kbd-then]:text-subtle-foreground",
        outline:
          "bg-background text-foreground shadow-button ring-1 ring-border ring-inset hover:bg-accent hover:ring-border-strong aria-expanded:bg-accent disabled:opacity-50 dark:bg-secondary dark:hover:bg-selected",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-selected aria-expanded:bg-selected disabled:opacity-50",
        ghost:
          "text-muted-foreground hover:bg-accent hover:text-foreground in-[.bg-popover]:hover:bg-popover-accent aria-expanded:bg-selected aria-expanded:text-foreground disabled:opacity-50",
        destructive:
          "bg-destructive-solid text-destructive-foreground shadow-button hover:bg-destructive-solid-hover focus-visible:ring-destructive/40 disabled:bg-muted disabled:text-subtle-foreground disabled:shadow-none **:data-[slot=kbd]:bg-destructive-foreground/15 **:data-[slot=kbd]:text-destructive-foreground **:data-[slot=kbd]:ring-0 **:data-[slot=kbd-then]:text-destructive-foreground/80 disabled:**:data-[slot=kbd]:bg-transparent disabled:**:data-[slot=kbd]:text-subtle-foreground disabled:**:data-[slot=kbd-then]:text-subtle-foreground",
        "destructive-ghost":
          "text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:text-destructive aria-expanded:bg-destructive/10 aria-expanded:text-destructive disabled:opacity-50",
        link: "text-brand-text underline-offset-4 hover:underline disabled:opacity-50",
      },
      // beacon: the 4px control-height ladder, xs 24 · sm 28 · default 32 · lg 36; sm and xs carry
      // 14px icons, the rest 16px. An icon given its own size class keeps it. On touch sm, default
      // and lg grow a step (36 · 40 · 44), and every size still under 44px reaches 44 with
      // `touch-target`.
      size: {
        xs: "h-6 px-2 text-xs touch-target has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        sm: "h-7 px-2.5 touch-target has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 touch:min-h-9 [&_svg:not([class*='size-'])]:size-3.5",
        default:
          "h-8 px-3 touch-target has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 touch:min-h-10 [&_svg:not([class*='size-'])]:size-4",
        lg: "h-9 px-4 text-prose has-data-[icon=inline-end]:pr-3.5 has-data-[icon=inline-start]:pl-3.5 touch:min-h-11 [&_svg:not([class*='size-'])]:size-4",
        "icon-xs": "size-6 touch-target [&_svg:not([class*='size-'])]:size-3.5",
        "icon-sm": "size-7 touch-target [&_svg:not([class*='size-'])]:size-4",
        icon: "size-8 touch-target [&_svg:not([class*='size-'])]:size-4",
        "icon-lg": "size-9 touch-target [&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
